import { Badge } from '../../components/ui';
import {
  IMPORT_CELL_COLUMNS,
  IMPORT_FLAG_LABEL,
  IMPORT_OUTCOME_LABEL,
  IMPORT_REASON_LABEL,
  importCellText,
  type CuratorPrefillImportRow,
  type ImportReportSection,
} from '../../api/curator-model';

/**
 * The report's section table (T.Z74, extracted from `PrefillImportModal` so the unit
 * suite can render it in plain Node — this module imports no stylesheet).
 *
 * THE COLUMNS ARE THE OWNER'S (16.09): the file's own five — ФИО, Номер заявления,
 * УНТИ ID, Регион, Программа/Курс (`IMPORT_CELL_COLUMNS`) — then the two verdict
 * columns «Итог» and «Пояснение». No «Строка» column: the row number carried no
 * information the operator could act on (the api echoes the row's own content now),
 * and dropping it gives the wide modal two columns back.
 *
 * THE ONE CSS RULE THIS TABLE LIVES BY: a cell here is a TABLE CELL. T.Z74's layout
 * bug came from the content cells carrying the shared `cell-sub` class, whose
 * `display: block` (theme.css) overrode `display: table-cell`; the browser's
 * anonymous-table fixup then folded every consecutive block-level cell of a row into
 * ONE anonymous cell, so the whole child's line rendered stacked inside the ФИО
 * column. `prefill-import__cell` styles the cell WITHOUT touching its display.
 */

/** Why the row looks the way it does: the api's reason plus its flags, in words.
 *  ADDITIVE (v1.9.46, T.Z166) — an `OFFLINE_FORMAT` refusal appends the площадка the
 *  child's офлайн-таблице lives on (`venueName`), the one fact the operator needs to
 *  go and check the right table. */
export function importRowExplanation(row: CuratorPrefillImportRow): string {
  const explanation = [
    row.reason === undefined ? null : IMPORT_REASON_LABEL[row.reason] ?? row.reason,
    ...(row.flags ?? []).map((flag) => IMPORT_FLAG_LABEL[flag] ?? flag),
  ].filter((part): part is string => part !== null && part !== '');
  const base = explanation.length > 0 ? explanation.join(', ') : '—';
  return row.venueName !== undefined && row.venueName !== '' ? `${base} · площадка «${row.venueName}»` : base;
}

/** The outcome's badge tone — green for a load, amber for a skip, red for a refusal. */
export function importOutcomeTone(outcome: string): 'ok' | 'warn' | 'bad' {
  if (outcome === 'IMPORTED') return 'ok';
  if (outcome === 'DUPLICATE') return 'warn';
  return 'bad';
}

export function ImportSection({ section }: { section: ImportReportSection }) {
  return (
    <details className="prefill-import__section" data-testid={`prefill-import-section-${section.key}`}>
      <summary>
        <span className="prefill-import__section-title">{section.title}</span>
        <Badge tone={importOutcomeTone(section.outcome)}>{section.count}</Badge>
      </summary>
      {section.rows.length === 0 ? (
        <p className="prefill-import__empty">Нет строк.</p>
      ) : (
        <table className="data prefill-import__table" data-testid={`prefill-import-table-${section.key}`}>
          <thead>
            <tr>
              {/* v1.9.10 — the row's own cells, so the operator sees WHAT the api did
                  with each line, not just which line. The same columns in all three
                  sections; an empty cell reads as a dash. T.Z74: no «Строка» column
                  and «Программа/Курс» as the fifth caption. */}
              {IMPORT_CELL_COLUMNS.map(([, caption]) => (
                <th key={caption} scope="col">
                  {caption}
                </th>
              ))}
              <th scope="col">Итог</th>
              <th scope="col">Пояснение</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row) => (
              <tr key={row.line} data-testid="prefill-import-row" data-outcome={row.outcome}>
                {IMPORT_CELL_COLUMNS.map(([key]) => (
                  <td key={key} className="prefill-import__cell">
                    {importCellText(row, key)}
                  </td>
                ))}
                <td>
                  <Badge tone={importOutcomeTone(row.outcome)}>
                    {IMPORT_OUTCOME_LABEL[row.outcome] ?? row.outcome}
                  </Badge>
                </td>
                <td className="prefill-import__cell">{importRowExplanation(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </details>
  );
}
